import { useEffect, useRef } from "react";
import utils from "@/utils/utils";

/**
 * iOS Safari 键盘收起后视图不复原的修复
 *
 */
export function useIOSKeyboardFix() {
  const isKeyboardOpenRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const scrollContainerRef = useRef<Element | null>(null);

  useEffect(() => {
    // 只在iOS设备上执行
    if (!utils.checkIsIos()) {
      return;
    }

    // 查找滚动容器
    const findScrollContainer = () => {
      return (
        document.querySelector(".mobile-layout__content") ||
        document.querySelector(".phone-wrapper") ||
        document.querySelector("#root")
      );
    };

    // 判断是否是输入元素
    const isInputElement = (element: HTMLElement | null): boolean => {
      if (!element) return false;
      const tagName = element.tagName;
      return (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        element.isContentEditable ||
        element.getAttribute("contenteditable") === "true"
      );
    };

    // 修复视图位置
    const fixViewportPosition = () => {
      // 方法1: 恢复滚动容器位置
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = lastScrollYRef.current;
      }

      // 方法2: 强制 window 滚动到顶部
      // 这是修复 iOS Safari 键盘收起后页面上移的关键
      window.scrollTo(0, 0);

      // 方法3: 触发重绘修复 visualViewport 偏移
      if (window.visualViewport && window.visualViewport.offsetTop > 0) {
        // 使用 requestAnimationFrame 确保在下一帧执行
        requestAnimationFrame(() => {
          document.body.style.height = "100.1%";
          requestAnimationFrame(() => {
            document.body.style.height = "";
          });
        });
      }
    };

    // 处理 focus 事件 - 记录键盘弹出前的滚动位置
    const handleFocus = (e: Event) => {
      const target = e.target as HTMLElement;
      if (isInputElement(target)) {
        isKeyboardOpenRef.current = true;
        // 记录当前滚动位置
        scrollContainerRef.current = findScrollContainer();
        if (scrollContainerRef.current) {
          lastScrollYRef.current = scrollContainerRef.current.scrollTop;
        }
      }
    };

    // 处理 blur 事件 - 键盘收起时恢复位置
    const handleBlur = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!isInputElement(target)) return;
      if (!isKeyboardOpenRef.current) return;

      // 延迟执行，等待键盘完全收起
      // 使用两次延迟确保在各种情况下都能正确执行
      setTimeout(() => {
        // 检查是否有新的焦点元素（避免在切换输入框时执行）
        const activeElement = document.activeElement as HTMLElement;
        if (isInputElement(activeElement)) {
          return;
        }

        isKeyboardOpenRef.current = false;
        fixViewportPosition();

        // 再次延迟执行，确保修复生效
        setTimeout(() => {
          if (!isKeyboardOpenRef.current) {
            fixViewportPosition();
          }
        }, 100);
      }, 50);
    };

    // 处理 visualViewport resize 事件
    const handleViewportResize = () => {
      if (!window.visualViewport) return;

      const viewport = window.visualViewport;
      const windowHeight = window.innerHeight;
      const viewportHeight = viewport.height;

      // 键盘收起的判断：viewport 高度接近或等于 window 高度
      // 且之前键盘是打开的
      const keyboardClosed =
        viewportHeight >= windowHeight - 50 && isKeyboardOpenRef.current;

      if (keyboardClosed) {
        // 检查是否有活跃的输入元素
        const activeElement = document.activeElement as HTMLElement;
        if (!isInputElement(activeElement)) {
          isKeyboardOpenRef.current = false;
          // 修复位置偏移
          setTimeout(fixViewportPosition, 50);
        }
      }
    };

    // 处理 touchend 事件 - 点击非输入区域时修复
    const handleTouchEnd = (e: TouchEvent) => {
      if (!isKeyboardOpenRef.current) return;

      const target = e.target as HTMLElement;
      // 如果点击的不是输入元素，延迟检查并修复
      if (!isInputElement(target)) {
        setTimeout(() => {
          const activeElement = document.activeElement as HTMLElement;
          if (!isInputElement(activeElement) && isKeyboardOpenRef.current) {
            // 强制失去焦点
            if (activeElement && typeof activeElement.blur === "function") {
              activeElement.blur();
            }
            isKeyboardOpenRef.current = false;
            fixViewportPosition();
          }
        }, 100);
      }
    };

    // 添加事件监听
    document.addEventListener("focusin", handleFocus, true);
    document.addEventListener("focusout", handleBlur, true);
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    // visualViewport API 支持检测
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportResize);
    }

    // 清理
    return () => {
      document.removeEventListener("focusin", handleFocus, true);
      document.removeEventListener("focusout", handleBlur, true);
      document.removeEventListener("touchend", handleTouchEnd);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleViewportResize
        );
      }
    };
  }, []);
}

export default useIOSKeyboardFix;
